import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Select, Skeleton, Tag, Tooltip, Typography, theme } from 'antd';
import type { RefSelectProps } from 'antd';
import { CloseOutlined, FilterOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { formatNumber } from '@leedpulse-monorepo/utils';
import type { MapPoint } from '../../../../domain/use_cases/leedflow/overview/getOverviewStats';
import { useTheme } from '../../../contexts/ui/ThemeContext';
import { EThemeNames } from '../../../../domain/types/themePresets';
import { MAP_HEAT_DARK, MAP_HEAT_LIGHT, MAP_HEAT_STEPS } from './chartColors';
import {
  CORSICA_PATH,
  FRANCE_MAINLAND_PATH,
  MAP_HEIGHT,
  MAP_WIDTH,
  project,
} from './franceGeo';

const { useToken } = theme;

const ZOOM_MAX = 4;

/** Past this, the tooltip is taller than the list it sits on. */
const TOOLTIP_IPS = 8;

/** One row per (point, subscription): the same subscription can sit in two cities. */
interface MapSearchOption {
  value: string;
  label: string;
  sender: string | null;
  city: string;
  users: number;
  search: string; // name + sender, what the input is matched against
}

interface UsersMapPanelProps {
  /** Utilisateurs mobiles par plateforme — la population qu'on cherche à placer. */
  devices: Record<string, number>;
  /** Points calculés côté backend à partir des adresses enregistrées. */
  points?: MapPoint[];
  /** Ajoute un abonnement au filtre de la page. */
  onSelectSubscription?: (subscriptionId: string) => void;

  status: ContextStatus;
}

// Points come from buildMapPoints (backend): a user is placed only when GeoLite2 names a city.
const UsersMapPanel: React.FC<UsersMapPanelProps> = ({
  devices,
  points = [],
  onSelectSubscription,
  status,
}) => {
  const { token } = useToken();
  const { themeName } = useTheme();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState<[number, number] | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragFrom = useRef<{ x: number; y: number; centerX: number; centerY: number } | null>(null);
  const dragged = useRef(false);
  const rowList = useRef<HTMLDivElement | null>(null);

  // The list scrolls: 49 subscriptions on Paris, and a highlight out of frame
  // signals nothing. Found by attribute and not by ref, because rc-util's
  // `useComposeRef` only recomputes when EVERY ref changed - the Tooltip's own
  // ref is stable, so a ref appearing on the child alone was silently dropped.
  // Scrolls the list and nothing else, where `scrollIntoView` moves the page too.
  useEffect(() => {
    const container = rowList.current;
    if (!container || !highlightedId) return;

    const row = container.querySelector<HTMLElement>(`[data-subscription="${CSS.escape(highlightedId)}"]`);
    if (!row) return;

    const offset = row.getBoundingClientRect().top - container.getBoundingClientRect().top;
    container.scrollTop += offset - (container.clientHeight - row.clientHeight) / 2;
  }, [selectedId, highlightedId]);

  const heatScale = themeName === EThemeNames.LEEDFLOW_DARK ? MAP_HEAT_DARK : MAP_HEAT_LIGHT;
  const stepOf = (count: number): number => MAP_HEAT_STEPS.filter((step) => count >= step.from).length - 1;

  const total = Object.values(devices).reduce((sum, value) => sum + value, 0);
  const placed = points.reduce((sum, point) => sum + point.users, 0);
  const selected = points.find((point) => point.id === selectedId) ?? null;

  const searchOptions: MapSearchOption[] = points
    .flatMap((point) =>
      point.subscriptions.map((entry) => ({
        value: `${point.id}|${entry.subscriptionId}`,
        label: entry.subscriptionName,
        sender: entry.sender,
        city: point.label,
        users: entry.users,
        search: `${entry.subscriptionName} ${entry.sender ?? ''}`,
      })),
    )
    .sort((a, b) => a.label.localeCompare(b.label) || a.city.localeCompare(b.city));

  // derived, so a point gone from the period leaves no dead option
  const searchValue = selected && highlightedId ? `${selected.id}|${highlightedId}` : null;

  // Chrome ignores rc-select's autocomplete="off", new-password it honours
  const stopAutofill = (node: RefSelectProps | null): void => {
    node?.nativeElement.querySelector('input')?.setAttribute('autocomplete', 'new-password');
  };

  const selectPoint = (pointId: string | null, subscriptionId: string | null = null): void => {
    setSelectedId(pointId);
    setHighlightedId(subscriptionId);
    // a manual drag froze the framing, the found point would stay off screen
    if (pointId) setCenter(null);
  };

  // Square root, not linear: the area carries the count. Capped, or one dense city
  // covers a region. Divided by the zoom: a mark keeps its size on screen, so
  // crowded points pull apart instead of growing together.
  const radiusOf = (count: number): number => Math.min(5.5, 0.9 + Math.sqrt(count) * 1.3) / zoom;

  const viewWidth = MAP_WIDTH / zoom;
  const viewHeight = MAP_HEIGHT / zoom;

  // Centred on the selected point, else on the densest one. Dragging takes over.
  const focus = selected ?? points[0];
  const [rawX, rawY] = center ?? (focus ? project(focus.lon, focus.lat) : [MAP_WIDTH / 2, MAP_HEIGHT / 2]);
  const inBounds = (value: number, span: number, max: number): number =>
    Math.min(Math.max(value, span / 2), max - span / 2);
  const centerX = inBounds(rawX, viewWidth, MAP_WIDTH);
  const centerY = inBounds(rawY, viewHeight, MAP_HEIGHT);
  const viewBox = [centerX - viewWidth / 2, centerY - viewHeight / 2, viewWidth, viewHeight].join(' ');

  const startDrag = (event: React.PointerEvent<SVGSVGElement>): void => {
    if (zoom === 1) return;
    dragFrom.current = { x: event.clientX, y: event.clientY, centerX, centerY };
    dragged.current = false;
    setIsDragging(true);
  };

  const moveDrag = (event: React.PointerEvent<SVGSVGElement>): void => {
    const from = dragFrom.current;
    if (!from) return;

    const unitsPerPixel = viewWidth / event.currentTarget.getBoundingClientRect().width;
    const dx = event.clientX - from.x;
    const dy = event.clientY - from.y;
    // A drag that ends on a point must not select it.
    if (Math.abs(dx) + Math.abs(dy) > 3) {
      // capture retargets the click to the <svg>: never before the drag is real
      if (!dragged.current) event.currentTarget.setPointerCapture(event.pointerId);
      dragged.current = true;
    }
    setCenter([from.centerX - dx * unitsPerPixel, from.centerY - dy * unitsPerPixel]);
  };

  const endDrag = (): void => {
    dragFrom.current = null;
    setIsDragging(false);
  };

  // Biggest painted last, so it sits in front.
  const drawOrder = [...points].sort((a, b) => a.users - b.users);

  return (
    <Card
      title="La carte"
      style={{ height: '100%' }}
      extra={
        status === ContextStatus.LOADING ? null : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {searchOptions.length > 0 && (
              <Select<string, MapSearchOption>
                showSearch
                allowClear
                size="small"
                style={{ width: 190 }}
                popupMatchSelectWidth={300}
                ref={stopAutofill}
                placeholder="Nom ou numéro..."
                options={searchOptions}
                value={searchValue}
                filterOption={(input, option) =>
                  (option?.search ?? '').toLowerCase().includes(input.toLowerCase())
                }
                optionRender={({ data }) => (
                  <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                    <span>{data.label}</span>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      {[data.sender, data.city, `${formatNumber(data.users)} util.`]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography.Text>
                  </div>
                )}
                onChange={(value) => {
                  const [pointId, subscriptionId] = value?.split('|') ?? [];
                  selectPoint(pointId ?? null, subscriptionId ?? null);
                }}
              />
            )}
            <Button
              type="text"
              size="small"
              icon={<ZoomInOutlined />}
              aria-label="Zoomer"
              disabled={zoom === ZOOM_MAX}
              onClick={() => setZoom((current) => Math.min(ZOOM_MAX, current + 1))}
            />
            <Button
              type="text"
              size="small"
              icon={<ZoomOutOutlined />}
              aria-label="Dézoomer"
              disabled={zoom === 1}
              onClick={() => {
                setZoom((current) => Math.max(1, current - 1));
                if (zoom - 1 === 1) setCenter(null);
              }}
            />
          </div>
        )
      }
    >
      {status === ContextStatus.LOADING ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <svg
            viewBox={viewBox}
            onPointerDown={startDrag}
            onPointerMove={moveDrag}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            // `role="img"` + `aria-label` est le motif recommandé pour un SVG
            // inline : la règle vise les `div role="img"` remplaçables par une
            // balise `img`, ce qu'un tracé dessiné ici ne peut pas être.
            // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
            role="img"
            aria-label="Carte de France des utilisateurs"
            style={{
              width: '100%',
              maxWidth: 360,
              height: 'auto',
              cursor: zoom === 1 ? 'default' : isDragging ? 'grabbing' : 'grab',
              // Zoomed, a touch drag pans the map instead of scrolling the page.
              touchAction: zoom === 1 ? 'auto' : 'none',
            }}
          >
            <path
              d={FRANCE_MAINLAND_PATH}
              fill={token.colorFillTertiary}
              stroke={token.colorBorderSecondary}
              strokeWidth={0.6 / zoom}
              strokeLinejoin="round"
            />
            <path
              d={CORSICA_PATH}
              fill={token.colorFillTertiary}
              stroke={token.colorBorderSecondary}
              strokeWidth={0.6 / zoom}
              strokeLinejoin="round"
            />

            {drawOrder.map((point) => {
              const [x, y] = project(point.lon, point.lat);
              const isSelected = point.id === selectedId;
              return (
                <Tooltip
                  key={point.id}
                  title={`${point.label} — ${formatNumber(point.users)} ${point.users > 1 ? 'utilisateurs' : 'utilisateur'}`}
                >
                  <circle
                    cx={x}
                    cy={y}
                    r={radiusOf(point.users)}
                    fill={heatScale[stepOf(point.users)]}
                    fillOpacity={isSelected ? 1 : 0.9}
                    // Surface ring: two overlapping points stay two points.
                    stroke={isSelected ? token.colorText : token.colorBgContainer}
                    strokeWidth={(isSelected ? 0.9 : 0.5) / zoom}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (dragged.current) return;
                      selectPoint(isSelected ? null : point.id);
                    }}
                  />
                </Tooltip>
              );
            })}
          </svg>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
            {MAP_HEAT_STEPS.map((step, index) => (
              <span key={step.from} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    backgroundColor: heatScale[index],
                  }}
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {step.label}
                </Typography.Text>
              </span>
            ))}
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              utilisateurs par lieu
            </Typography.Text>
          </div>

          {/* CC-BY 4.0: the dataset may not be shown without this credit. */}
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            <a href="https://db-ip.com" target="_blank" rel="noreferrer">
              IP Geolocation by DB-IP
            </a>
          </Typography.Text>

          <div style={{ textAlign: 'center' }}>
            <span
              style={{
                fontSize: 24,
                fontWeight: 700,
                color: placed > 0 ? token.colorPrimary : token.colorText,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatNumber(placed)}
            </span>
            <span style={{ color: token.colorTextSecondary }}>
              {' '}sur {formatNumber(total)} utilisateurs localisés
            </span>
          </div>

          {/* Le détail au clic plutôt que dans l'infobulle : une infobulle ne
              retient pas le pointeur, on ne peut donc rien y cliquer. */}
          {selected && (
            <div
              style={{
                width: '100%',
                borderTop: `1px solid ${token.colorBorderSecondary}`,
                paddingTop: 12,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                <Typography.Text strong>
                  {selected.label}{' '}
                  <Tag style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatNumber(selected.users)}
                  </Tag>
                </Typography.Text>
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => selectPoint(null)}
                  aria-label="Fermer le détail"
                />
              </div>

              <div
                ref={rowList}
                style={{ display: 'flex', flexDirection: 'column', maxHeight: 220, overflowY: 'auto' }}
              >
                {selected.subscriptions.map((entry) => {
                  const isHighlighted = entry.subscriptionId === highlightedId;
                  return (
                    <Tooltip
                      key={entry.subscriptionId}
                      title={
                        <div style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {entry.ips.slice(0, TOOLTIP_IPS).map((ip) => (
                            <div key={ip}>{ip}</div>
                          ))}
                          {entry.ips.length > TOOLTIP_IPS && (
                            <div>+{formatNumber(entry.ips.length - TOOLTIP_IPS)} autres</div>
                          )}
                        </div>
                      }
                    >
                      <Button
                        key={entry.subscriptionId}
                        data-subscription={entry.subscriptionId}
                        type="text"
                        onClick={() => onSelectSubscription?.(entry.subscriptionId)}
                        // Une grille et non un flex libre : le nom occupe la
                        // colonne souple, l'effectif la colonne fixe, donc les
                        // nombres s'alignent d'une ligne à l'autre au lieu de
                        // flotter derrière des libellés de longueurs différentes.
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '16px 1fr auto',
                          alignItems: 'center',
                          gap: 8,
                          height: 'auto',
                          padding: '6px 8px',
                          textAlign: 'left',
                          backgroundColor: isHighlighted ? token.colorFillSecondary : undefined,
                        }}
                        block
                      >
                        <FilterOutlined style={{ color: token.colorTextTertiary }} />
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={entry.subscriptionName}
                        >
                          {entry.subscriptionName}
                        </span>
                        <span style={{ color: token.colorTextSecondary, fontVariantNumeric: 'tabular-nums' }}>
                          {formatNumber(entry.users)}
                        </span>
                      </Button>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          )}

          {points.length === 0 && (
            <Typography.Text type="secondary" style={{ textAlign: 'center', maxWidth: 380 }}>
              Aucune position à afficher.
            </Typography.Text>
          )}
        </div>
      )}
    </Card>
  );
};

export default UsersMapPanel;
